import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, doc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './Dashboard.css';

const Dashboard = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, postId: null, postTitle: '' });
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'blogposts'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(q);
      const posts = [];
      
      querySnapshot.forEach((doc) => {
        posts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setBlogPosts(posts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setError('Failed to load blog posts');
      setLoading(false);
    }
  };


  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const handleBlogClick = (post) => {
    // Navigate to edit page with blog post data
    navigate('/create-post', { 
      state: { 
        editMode: true, 
        blogPost: post 
      } 
    });
  };

  const handleDeleteClick = (e, post) => {
    e.stopPropagation(); // Prevent card click event
    setDeleteConfirm({
      show: true,
      postId: post.id,
      postTitle: post.title
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.postId) return;

    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'blogposts', deleteConfirm.postId));
      
      // Remove from local state
      setBlogPosts(prev => prev.filter(post => post.id !== deleteConfirm.postId));
      
      setDeleteConfirm({ show: false, postId: null, postTitle: '' });
      setError('');
    } catch (error) {
      console.error('Error deleting blog post:', error);
      setError('Failed to delete blog post. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, postId: null, postTitle: '' });
  };

  return (
    <div className="dashboard-wrapper">
      {/* Main Content */}
      <main className="dashboard-main">
        <div className="content-wrapper">
          <div className="section-header">
            <h2>All Blog Posts</h2>
            <p className="post-count">{blogPosts.length} posts published</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading-posts">
              <p>Loading blog posts...</p>
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="no-posts">
              <h3>No blog posts yet</h3>
              <p>Start creating your first blog post!</p>
              <button onClick={() => navigate('/create-post')} className="create-first-post-btn">
                Create Your First Post
              </button>
            </div>
          ) : (
            <div className="blog-grid">
              {blogPosts.map((post) => (
                <article 
                  key={post.id} 
                  className="blog-card clickable"
                  onClick={() => handleBlogClick(post)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBlogClick(post);
                    }
                  }}
                  aria-label={`Edit blog post: ${post.title}`}
                >
                  {/* Delete Button */}
                  <button
                    className="delete-post-btn"
                    onClick={(e) => handleDeleteClick(e, post)}
                    title="Delete post"
                    aria-label={`Delete post: ${post.title}`}
                  >
                    🗑️
                  </button>

                  {/* Blog Image */}
                  <div className="blog-image-container">
                    {post.images?.mainImage ? (
                      <img 
                        src={post.images.mainImage} 
                        alt={post.title}
                        className="blog-image"
                      />
                    ) : (
                      <div className="blog-image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}
                  </div>

                  {/* Blog Content */}
                  <div className="blog-content">
                    {/* Category Tags */}
                    <div className="blog-tags">
                      {post.category && (
                        <span className="tag category-tag">{post.category}</span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="blog-title">{post.title}</h3>

                    {/* Excerpt */}
                    <p className="blog-excerpt">{post.excerpt}</p>

                    {/* SEO Keywords */}
                    {post.seoKeywords && (
                      <div className="seo-keywords-section">
                        <span className="seo-label">🔍 SEO Keywords:</span>
                        <span className="seo-keywords">{post.seoKeywords}</span>
                      </div>
                    )}

                    {/* Meta Information */}
                    <div className="blog-meta">
                      <div className="author-info">
                        <img 
                          src={post.author?.photoURL || 'https://via.placeholder.com/40'} 
                          alt={post.author?.name}
                          className="author-avatar"
                        />
                        <div className="author-details">
                          <span className="author-name">By {post.author?.name}</span>
                          <span className="post-date">{formatDate(post.createdAt)}</span>
                        </div>
                      </div>
                      
                      <div className="post-stats">
                        <span className="stat">{post.views || 0} views</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Post</h3>
            <p>Are you sure you want to delete <strong>"{deleteConfirm.postTitle}"</strong>?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="modal-btn cancel-btn"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="modal-btn delete-btn"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
